import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useSetInternalDev } from "../hooks/useAdminAccounts";
import type { AdminAccountSearchItem } from "../types/account.types";

interface AccountResultTableProps {
  accounts: AdminAccountSearchItem[];
  isLoading: boolean;
  hasKeyword: boolean;
  onMoveCompany: (account: AdminAccountSearchItem) => void;
}

export function AccountResultTable({
  accounts,
  isLoading,
  hasKeyword,
  onMoveCompany,
}: AccountResultTableProps) {
  const internalDevMutation = useSetInternalDev();

  if (!hasKeyword) {
    return (
      <p className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-xs text-gray-500">
        로그인 아이디를 입력해 계정을 찾으세요.
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-xs text-gray-500">
        일치하는 계정이 없습니다.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="w-full min-w-[900px] text-xs">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            <th className="px-3 py-2 text-left font-medium">아이디</th>
            <th className="px-3 py-2 text-left font-medium">이름</th>
            <th className="px-3 py-2 text-left font-medium">소속 업체</th>
            <th className="px-3 py-2 text-left font-medium">부서</th>
            <th className="px-3 py-2 text-left font-medium">상태</th>
            <th className="px-3 py-2 text-left font-medium">최근 로그인</th>
            <th className="px-3 py-2 text-center font-medium">내부 개발자</th>
            <th className="px-3 py-2 text-center font-medium">업체 변경</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {accounts.map((account) => (
            <tr key={account.accountGuid} className="hover:bg-gray-50/60">
              <td className="px-3 py-2 font-medium text-gray-900">
                {account.accountId}
              </td>
              <td className="px-3 py-2 text-gray-700">{account.name}</td>
              <td className="px-3 py-2 text-gray-700">
                {account.companyName}
                <span className="ml-1.5 text-[11px] text-gray-400">
                  {account.companyServiceRange.join(", ")}
                </span>
              </td>
              <td className="px-3 py-2 text-gray-500">
                {account.department || "-"}
              </td>
              <td className="px-3 py-2">
                {account.isPause ? (
                  <span className="rounded bg-red-100 px-1.5 py-0.5 text-[11px] text-red-700">
                    중지
                  </span>
                ) : account.isActive ? (
                  <span className="rounded bg-green-100 px-1.5 py-0.5 text-[11px] text-green-700">
                    활성
                  </span>
                ) : (
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">
                    비활성
                  </span>
                )}
              </td>
              <td className="px-3 py-2 text-gray-500">
                {account.lastLoginTime
                  ? format(new Date(account.lastLoginTime), "yyyy-MM-dd HH:mm")
                  : "-"}
              </td>
              <td className="px-3 py-2 text-center">
                <Switch
                  checked={account.isInternalDev}
                  disabled={internalDevMutation.isPending}
                  onCheckedChange={(checked) =>
                    internalDevMutation.mutate({
                      AccountGuid: account.accountGuid,
                      IsInternalDev: checked,
                    })
                  }
                />
              </td>
              <td className="px-3 py-2 text-center">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onMoveCompany(account)}
                  className="h-7 px-2 text-[11px]"
                >
                  변경
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
